import { useMemo, useState } from 'react';
import { Box, Typography, Tooltip, CircularProgress, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useQuery } from '@tanstack/react-query';
import { api, rpcCall } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { gwtStyles } from '../../config/theme';
import { useUIStore } from '../../stores/uiStore';
import type { Study, DicomObject } from '../../api/types';
import type { UserPreferences } from '../preferences/usePreferences';

/**
 * GWT-matching detail panel — mirrors DetailPanel.java.
 *
 * Shows details about the selected study in a 4×3 field grid.
 *
 * Field layout (from DetailPanel.java):
 *   Row 0: Patient Name  | Date             | History
 *   Row 1: Patient ID    | Status           | Reason for Exam
 *   Row 2: Accession #   | Urgent Action    | Urgent Reason
 *   Row 3: Final Read    | Physician        | Contact Location
 *
 * CSS:
 *   .brit-detail-panel { background-color: #e2d6c7; }
 *   .brit-detail-field { text-align: right; font-size: 105%; margin/padding: 1px }
 *   .brit-detail-value { border-radius: 3px; font-weight: bold; background: #F3F7FB; }
 *
 * Below the field grid: thumbnail bar (IconScrollPanel) with WADO-URI thumbnails.
 */

/** Derive finalRead from study.details.contact.finalRead (matches GWT StudyBean.getFinalRead) */
function getFinalRead(study: Study): string {
  const details = study.details;
  if (!details) return 'false';
  const contact = details.contact as Record<string, unknown> | undefined;
  if (!contact) return 'false';
  return String(contact.finalRead ?? false);
}

/** All known detail field definitions with extractors */
const FIELD_MAP: Record<string, { label: string; extract: (s: Study) => string }> = {
  patientName: { label: 'Patient Name', extract: (s) => s.niceName || s.name || '' },
  date:        { label: 'Date', extract: (s) => s.date || '' },
  history:     { label: 'History', extract: (s) => s.history || '' },
  patientId:   { label: 'Patient ID', extract: (s) => s.id || '' },
  status:      { label: 'Status', extract: (s) => s.status || '' },
  reason:      { label: 'Reason for Exam', extract: (s) => s.reason || '' },
  accession:   { label: 'Accession #', extract: (s) => s.acc || '' },
  urgentAction:{ label: 'Urgent Action', extract: (s) => String(s.urgent?.actionNeeded ?? false) },
  urgentReason:{ label: 'Urgent Reason', extract: (s) => s.urgent?.reason || '' },
  finalRead:   { label: 'Final Read', extract: getFinalRead },
  physician:   { label: 'Physician', extract: (s) => s.phyName || '' },
  contactLocation: { label: 'Contact Location', extract: () => '' },
};

const DEFAULT_FIELD_ORDER = ['patientName','date','history','patientId','status','reason','accession','urgentAction','urgentReason','finalRead','physician','contactLocation'];

/** Build a flat list of visible fields based on configured order */
function buildConfigurableGrid(study: Study | null, fieldOrder: string[]): { label: string; value: string }[] {
  // Filter to only visible fields (no '-' prefix)
  const visibleIds = fieldOrder.filter((f) => !f.startsWith('-')).map((f) => f.replace(/^-/, ''));
  return visibleIds.map((id) => {
    const def = FIELD_MAP[id];
    if (!def) return { label: id, value: '' };
    return { label: def.label, value: study ? def.extract(study) : '' };
  });
}

export const DetailPanel = () => {
  const selectedStudies = useUIStore((s) => s.selectedStudies);
  const hasSelection = selectedStudies.length > 0;

  // Show last selected study (or first if multiple)
  const study = hasSelection ? selectedStudies[selectedStudies.length - 1] : null;

  // Load user's detail field preferences (from server, cached)
  const { data: savedPrefs } = useQuery({
    queryKey: ['userPrefs', 'detailPanelFields'],
    queryFn: async () => {
      try {
        const result = await rpcCall<UserPreferences | null>('getjson', { key: 'userPreferences' });
        return result?.detailPanelFields ?? null;
      } catch { return null; }
    },
    staleTime: 60_000,
  });
  const fieldOrder = savedPrefs ?? DEFAULT_FIELD_ORDER;

  const fields = useMemo(() => buildConfigurableGrid(study, fieldOrder), [study, fieldOrder]);

  // Multi-selection indicator
  const multiLabel = selectedStudies.length > 1
    ? `${selectedStudies.length} studies selected`
    : null;

  return (
    <Box
      sx={{
        height: '100%',
        backgroundColor: gwtStyles.detailPanel,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Multi-selection banner */}
      {multiLabel && (
        <Box sx={{ px: 1, py: 0.25, fontSize: 11, fontWeight: 600, color: 'text.secondary', textAlign: 'center' }}>
          {multiLabel}
        </Box>
      )}

      {/* Field grid — 3-column CSS grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1px',
          p: 0.5,
          flexShrink: 0,
          minHeight: 0,
        }}
      >
        {fields.map((field) => (
          <DetailFieldCell
            key={field.label}
            label={field.label}
            value={field.value}
          />
        ))}
      </Box>

      {/* Linked / Prior Studies */}
      <LinkedStudiesBar study={study} />

      {/* Thumbnail bar — loads DICOM object thumbnails via getObjects */}
      <ThumbnailBar study={study} />
    </Box>
  );
};

/** Collapsible linked / prior studies strip */
const LinkedStudiesBar = ({ study }: { study: Study | null }) => {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.linkedStudies(study?.objKey ?? ''),
    queryFn: () => api.getLinkedStudies(study!.objKey, true),
    enabled: !!study?.objKey,
    staleTime: 60_000,
  });
  const linked = data?.records ?? [];

  // Don't render anything when no study selected or no linked studies
  if (!study) return null;
  if (!isLoading && linked.length === 0) return null;

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.25,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, fontSize: 11, color: 'text.secondary' }}>
          {isLoading ? 'Loading linked studies…' : `Linked Studies (${linked.length})`}
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ maxHeight: 120, overflowY: 'auto', px: 1, pb: 0.5 }}>
          {linked.map((ls) => (
            <Box
              key={ls.objKey ?? ls.uid}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'baseline',
                fontSize: 11,
                py: 0.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11, minWidth: 70 }}>
                {ls.date || '—'}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ls.descr || ls.mod || '—'}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
                {ls.acc || ''}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const DetailFieldCell = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
    {/* Label — right-aligned, matching .brit-detail-field */}
    <Typography
      variant="caption"
      sx={{
        fontSize: '105%',
        textAlign: 'right',
        whiteSpace: 'nowrap',
        minWidth: 90,
        flexShrink: 0,
        m: '1px',
        p: '1px',
        color: 'text.secondary',
      }}
    >
      {label}:
    </Typography>
    {/* Value — bold, on #F3F7FB background, matching .brit-detail-value */}
    <Box
      sx={{
        flex: 1,
        backgroundColor: gwtStyles.detailValue,
        borderRadius: '3px',
        m: '1px',
        p: '1px 4px',
        minHeight: 20,
        fontSize: '105%',
        fontWeight: 'bold',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
      >
        {value || '\u00A0'}
      </Typography>
    </Box>
  </Box>
);

/**
 * Extract the bare site ID from a site objKey.
 * e.g. "site:root" → "root", "root" → "root"
 */
function extractSiteId(siteKey: string | undefined): string {
  if (!siteKey) return 'root';
  return siteKey.startsWith('site:') ? siteKey.substring(5) : siteKey;
}

/**
 * Build a WADO-URI thumbnail URL for a DICOM object.
 * Uses /PacsObjectService/{siteId} endpoint matching GWT's getWadoUrl().
 */
function buildThumbnailUrl(study: Study, obj: DicomObject): string {
  const session = localStorage.getItem('brit_sid') ?? '';
  const siteId = extractSiteId(study.site ?? study.siteId);
  if (obj.seruid && obj.id) {
    const params = new URLSearchParams({
      requestType: 'WADO',
      studyUID: study.uid,
      seriesUID: obj.seruid,
      objectUID: obj.id,
      contentType: 'image/jpeg',
      rows: '160',
    });
    if (session) params.set('session', session);
    return `/PacsObjectService/${encodeURIComponent(siteId)}?${params.toString()}`;
  }
  return '';
}

/** Horizontal scrollable thumbnail strip at the bottom of the detail panel */
const ThumbnailBar = ({ study }: { study: Study | null }) => {
  const { data: objectsResult, isLoading } = useQuery({
    queryKey: queryKeys.objects(study?.objKey ?? ''),
    queryFn: () => api.getObjects(study!.objKey),
    enabled: !!study?.objKey,
    staleTime: 60_000,
  });
  const objects = objectsResult?.records;

  // Group by series — show one thumbnail per series, skip non-image objects.
  const seriesThumbnails = useMemo(() => {
    if (!objects || !study) return [];
    const grouped = new Map<string, DicomObject[]>();
    for (const obj of objects) {
      // Skip report/SR/non-image series that WADO can't render as JPEG
      if (obj.seruid === 'Report' || obj.seruid === 'SR') continue;
      const key = obj.seruid ?? obj.id;
      const existing = grouped.get(key) ?? [];
      existing.push(obj);
      grouped.set(key, existing);
    }
    return Array.from(grouped.values())
      .map(seriesObjects => {
        const representative = seriesObjects[Math.floor(seriesObjects.length / 2)] ?? seriesObjects[0];
        return { representative, imageCount: seriesObjects.length };
      })
      .sort((left, right) => {
        const leftDesc = String(left.representative.details?.sdes ?? '').toLowerCase();
        const rightDesc = String(right.representative.details?.sdes ?? '').toLowerCase();
        const leftScout = leftDesc.includes('scout') || leftDesc.includes('localizer') || leftDesc.includes('topogram');
        const rightScout = rightDesc.includes('scout') || rightDesc.includes('localizer') || rightDesc.includes('topogram');
        if (leftScout !== rightScout) return leftScout ? 1 : -1;
        return (left.representative.snum ?? 0) - (right.representative.snum ?? 0);
      });
  }, [objects, study]);

  return (
    <Box
      sx={{
        height: 108,
        flexShrink: 0,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'stretch',
        px: 1,
        gap: 0.5,
        overflowX: 'auto',
        overflowY: 'hidden',
        py: 0.75,
      }}
    >
      {!study && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
          Select a study to view thumbnails
        </Typography>
      )}
      {study && isLoading && <CircularProgress size={16} />}
      {study && seriesThumbnails.length === 0 && !isLoading && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
          No images
        </Typography>
      )}
      {study &&
        seriesThumbnails.map(({ representative, imageCount }) => {
          const url = buildThumbnailUrl(study, representative);
          const seriesLabel = representative.details?.sdes ?? `Series ${representative.snum ?? ''}`.trim();
          return (
            <Tooltip
              key={representative.id}
              title={`${seriesLabel} (${imageCount} image${imageCount === 1 ? '' : 's'})`}
            >
              <Box
                sx={{
                  width: 92,
                  height: 92,
                  borderRadius: '3px',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: '#151924',
                  flexShrink: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={url || undefined}
                  alt={seriesLabel || 'thumbnail'}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    const img = e.currentTarget;
                    img.style.objectFit = 'contain';
                    img.src = 'data:image/svg+xml,' + encodeURIComponent(
                      '<svg xmlns="http://www.w3.org/2000/svg" width="84" height="68" viewBox="0 0 84 68"><rect fill="%23151924" width="84" height="68"/><text x="42" y="38" text-anchor="middle" fill="%236f7b91" font-size="11">N/A</text></svg>'
                    );
                  }}
                  sx={{
                    width: '100%',
                    height: 72,
                    objectFit: 'contain',
                    backgroundColor: '#000',
                    px: 0.25,
                    pt: 0.25,
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    backgroundColor: '#20263a',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 10,
                      lineHeight: 1,
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {imageCount} image{imageCount === 1 ? '' : 's'}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        })}
    </Box>
  );
};
